import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-background-100">
            <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-foreground-600 mb-6">您要查看的文章不存在或已被删除</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors inline-block whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '好博体育',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://haobo-sports.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 好博体育`}
        description={article.excerpt}
        keywords={`好博体育,${article.categoryName},${article.tags.join(',')}`}
        canonicalUrl={`https://haobo-sports.com/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        schemaJson={articleSchema}
      />

      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="max-w-[900px] mx-auto px-4 md:px-6 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              {article.tags.slice(0, 2).map((tag) => (
                <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span className="text-foreground-300">|</span>
              <span>{article.date}</span>
              <span className="text-foreground-300">|</span>
              <span>{article.readTime}</span>
            </div>
          </div>

          <div className="relative h-[400px] md:h-[500px] rounded-xl overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`好博体育 - ${article.title}`}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base
              prose-p:mb-5
              prose-strong:text-foreground-900 prose-strong:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200/70 mt-12 pt-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-secondary-50 border border-secondary-200/70 rounded-full text-xs text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-200/70">
            <Link
              to={`/category/${article.category}`}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回{article.categoryName}列表
            </Link>
            <div className="flex items-center gap-3">
              <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 hover:bg-primary-50 hover:border-primary-300 transition-colors" aria-label="分享到微信">
                <i className="ri-wechat-line text-foreground-600"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 hover:bg-primary-50 hover:border-primary-300 transition-colors" aria-label="分享到微博">
                <i className="ri-weibo-line text-foreground-600"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 hover:bg-primary-50 hover:border-primary-300 transition-colors" aria-label="复制链接">
                <i className="ri-link text-foreground-600"></i>
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/70 py-12 md:py-16">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all overflow-hidden flex flex-col sm:flex-row"
                  >
                    <div className="relative w-full sm:w-40 h-36 sm:h-auto overflow-hidden flex-shrink-0">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 flex-1 flex flex-col justify-center">
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span>{related.author}</span>
                        <span>{related.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}